import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Checkbox,
    Paper,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardContent,
    Tooltip,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Badge,
    CircularProgress,
    Fab,
    Zoom,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    ButtonGroup,
    Divider,
    useMediaQuery,
    useTheme,
    Fade,
    Grow,
    Container
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { getISOWeek, startOfWeek, endOfWeek, format, addWeeks, subWeeks, setWeek, setYear } from 'date-fns';
import { initializeDB } from '../../config/db';
import { EMPLOYEE_STORE, TEMPS_STORE, WEEK_LIST_STORE, DB_NAME, DB_VERSION } from '../../config/constants';
import weeklyRosterService from '../../services/weeklyRosterService';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TodayIcon from '@mui/icons-material/Today';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';


const WeeklyRosterImproved = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    
    // State
    const [employees, setEmployees] = useState([]);
    const [temps, setTemps] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [currentWeekNumber, setCurrentWeekNumber] = useState(getISOWeek(new Date()));
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasCurrentWeekRoster, setHasCurrentWeekRoster] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [filterDivision, setFilterDivision] = useState('all');
    const [filterAgency, setFilterAgency] = useState('all');
    const [userData, setUserData] = useState(null);
    
    // Check if current week is the actual current week
    const isCurrentWeek = useMemo(() => {
        const now = new Date();
        const actualWeekNumber = getISOWeek(now);
        const actualYear = now.getFullYear();
        return currentWeekNumber === actualWeekNumber && currentYear === actualYear;
    }, [currentWeekNumber, currentYear]);

    // Get unique divisions for permanent employees
    const divisions = useMemo(() => {
        const uniqueDivisions = [...new Set(employees.map(emp => 
            emp.Division || emp.division || emp.DivisionCode
        ).filter(Boolean))];
        return uniqueDivisions.sort();
    }, [employees]);

    // Get unique agencies for temporary employees
    const agencies = useMemo(() => {
        const uniqueAgencies = [...new Set(temps.map(temp => 
            temp.Agency || temp.agency
        ).filter(Boolean))];
        return uniqueAgencies.sort();
    }, [temps]);

    // Current week dates - Fixed calculation
    const currentWeekDates = useMemo(() => {
        // Create a date object for the specific week and year
        let date = new Date();
        date = setYear(date, currentYear);
        date = setWeek(date, currentWeekNumber, { weekStartsOn: 1 });
        
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
        
        return {
            start: format(weekStart, 'MMM dd'),
            end: format(weekEnd, 'MMM dd, yyyy')
        };
    }, [currentWeekNumber, currentYear]);

    // Get all employees (permanent + temps) for display
    const allEmployeesMap = useMemo(() => {
        const map = new Map();
        employees.forEach(emp => map.set(emp.id, { ...emp, type: 'permanent' }));
        temps.forEach(temp => map.set(temp.id, { ...temp, type: 'temp' }));
        return map;
    }, [employees, temps]);

    // Notification handler
    const showNotification = useCallback((message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    }, []);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);

        // Clear selected employees FIRST to prevent ghost data
        setSelectedEmployees([]);
        setHasCurrentWeekRoster(false);

        try {
            const db = await initializeDB();

            // Load permanent employees
            try {
                const empTx = db.transaction(EMPLOYEE_STORE, 'readonly');
                const empStore = empTx.objectStore(EMPLOYEE_STORE);
                const allEmployees = await empStore.getAll();
                setEmployees(allEmployees);
            } catch (error) {
                console.error('Error loading employees:', error);
                setEmployees([]);
            }

            // Load temporary employees
            try {
                const tempTx = db.transaction(TEMPS_STORE, 'readonly');
                const tempStore = tempTx.objectStore(TEMPS_STORE);
                const allTemps = await tempStore.getAll();
                setTemps(allTemps);
            } catch (error) {
                console.error('Error loading temps:', error);
                setTemps([]);
            }

            // Get current user ID
            const userDataStr = localStorage.getItem('user');
            let userId = null;
            let user = null;

            if (userDataStr) {
                try {
                    user = JSON.parse(userDataStr);
                    userId = user.id || user.userId || user.employeeId;
                    setUserData(user);
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }

            // Load weekly list for current week
            const weekYear = `${currentYear}-W${String(currentWeekNumber).padStart(2, '0')}`;

            // FIRST: Check IndexedDB
            const tx = db.transaction(WEEK_LIST_STORE, 'readonly');
            const store = tx.objectStore(WEEK_LIST_STORE);
            const localRoster = await store.get(weekYear);
            await tx.done;

            if (localRoster) {
                // Found in IndexedDB - use it directly, no server request
                if (localRoster.permanentEmployees || localRoster.temporaryEmployees) {
                    const allSelected = [
                        ...(localRoster.permanentEmployees || []),
                        ...(localRoster.temporaryEmployees || [])
                    ];
                    setSelectedEmployees(allSelected);
                } else if (localRoster.employees) {
                    setSelectedEmployees(localRoster.employees || []);
                }
                setHasCurrentWeekRoster(true);
            } else {
                // Not in IndexedDB - try server (only once, no spam)
                try {
                    const serverRoster = await weeklyRosterService.getRoster(weekYear);

                    // Check if serverRoster exists and has required weekYear field
                    if (serverRoster && serverRoster.weekYear) {
                        // Save to IndexedDB only if we have valid data
                        const saveTx = db.transaction(WEEK_LIST_STORE, 'readwrite');
                        const saveStore = saveTx.objectStore(WEEK_LIST_STORE);
                        await saveStore.put({
                            weekYear: serverRoster.weekYear,
                            weekNumber: serverRoster.weekNumber,
                            year: serverRoster.year,
                            employees: serverRoster.employees,
                            permanentEmployees: serverRoster.permanentEmployees,
                            temporaryEmployees: serverRoster.temporaryEmployees,
                            userId: serverRoster.userId,
                            createdBy: serverRoster.createdBy,
                            supervisorId: serverRoster.supervisorId,
                            supervisorName: serverRoster.supervisorName,
                            dateCreated: serverRoster.dateCreated,
                            dateModified: serverRoster.dateModified
                        });
                        await saveTx.done;

                        // Set selected employees
                        const allSelected = [
                            ...(serverRoster.permanentEmployees || []),
                            ...(serverRoster.temporaryEmployees || [])
                        ];
                        setSelectedEmployees(allSelected);
                        setHasCurrentWeekRoster(true);
                    }
                    // If server returns null or empty object - do nothing, selectedEmployees already cleared
                } catch (error) {
                    // Silently ignore 404 and 204 errors - no roster exists yet
                    if (error.response?.status !== 404 && error.response?.status !== 204) {
                        console.error('Error loading roster from server:', error);
                    }
                    // selectedEmployees already cleared, leave empty
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
            showNotification('Error loading data', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentWeekNumber, currentYear, showNotification]);

    // Sync last 10 weeks on component mount
    const syncLastWeeks = async () => {
        try {
            console.log('Syncing last 10 weeks from server...');
            const lastWeeks = await weeklyRosterService.getLastWeeks(10);
            
            if (lastWeeks && lastWeeks.length > 0) {
                // Get current user information
                const userData = localStorage.getItem('user');
                let userId = null;
                
                if (userData) {
                    try {
                        const user = JSON.parse(userData);
                        userId = user.id || user.userId || user.employeeId;
                    } catch (e) {
                        console.error('Error parsing user data:', e);
                    }
                }
                
                const db = await initializeDB();
                const tx = db.transaction(WEEK_LIST_STORE, 'readwrite');
                const store = tx.objectStore(WEEK_LIST_STORE);
                
                // Sync all rosters for now
                let syncedCount = 0;
                for (const roster of lastWeeks) {
                    await store.put({
                        weekYear: roster.weekYear,
                        weekNumber: roster.weekNumber,
                        year: roster.year,
                        employees: roster.employees,
                        permanentEmployees: roster.permanentEmployees,
                        temporaryEmployees: roster.temporaryEmployees,
                        userId: roster.userId,
                        createdBy: roster.createdBy,
                        supervisorId: roster.supervisorId,
                        supervisorName: roster.supervisorName,
                        dateCreated: roster.dateCreated,
                        dateModified: roster.dateModified
                    });
                    syncedCount++;
                }
                
                await tx.done;
                console.log(`Synced ${syncedCount} user-specific weeks from server (out of ${lastWeeks.length} total)`);
            }
        } catch (error) {
            console.error('Error syncing last weeks:', error);
        }
    };

    useEffect(() => {
        // Load user data on mount
        const userDataStr = localStorage.getItem('user');
        if (userDataStr) {
            try {
                const user = JSON.parse(userDataStr);
                setUserData(user);
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }
        
        // First sync last weeks, then load current data
        syncLastWeeks().then(() => {
            loadData();
        });
    }, []);

    // Load data when week changes
    useEffect(() => {
        loadData();
    }, [currentWeekNumber, currentYear, loadData]);

    // Copy current viewed week to actual current week
    const copyThisWeekToCurrent = async () => {
        if (isCurrentWeek) {
            showNotification('Already on current week', 'warning');
            return;
        }

        if (selectedEmployees.length === 0) {
            showNotification('No employees to copy from this week', 'warning');
            return;
        }

        try {
            // Get actual current week
            const now = new Date();
            const actualWeekNumber = getISOWeek(now);
            const actualYear = now.getFullYear();
            const currentWeekYear = `${actualYear}-W${String(actualWeekNumber).padStart(2, '0')}`;

            // Prepare roster data for current week
            const db = await initializeDB();

            // Get current user information
            const userData = localStorage.getItem('user');
            let userId = null;
            let supervisorName = 'Unknown';

            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    userId = user.id || user.userId || user.employeeId;
                    supervisorName = user.name || user.fullName || user.firstName + ' ' + user.lastName || 'Unknown';
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }

            // Separate selected employees into permanent and temp
            const permanentSelected = [];
            const tempSelected = [];

            selectedEmployees.forEach(id => {
                const employee = allEmployeesMap.get(id);
                if (employee) {
                    if (employee.type === 'temp') {
                        tempSelected.push(id);
                    } else {
                        permanentSelected.push(id);
                    }
                }
            });

            const rosterData = {
                weekYear: currentWeekYear,
                weekNumber: actualWeekNumber,
                year: actualYear,
                employees: selectedEmployees,
                permanentEmployees: permanentSelected,
                temporaryEmployees: tempSelected,
                userId: userId,
                createdBy: userId,
                supervisorId: userId,
                supervisorName: supervisorName,
                dateCreated: new Date().toISOString(),
                dateModified: new Date().toISOString(),
                employeeCount: selectedEmployees.length,
                permanentCount: permanentSelected.length,
                tempCount: tempSelected.length
            };

            // Save to IndexedDB
            const tx = db.transaction(WEEK_LIST_STORE, 'readwrite');
            const store = tx.objectStore(WEEK_LIST_STORE);
            await store.put(rosterData);
            await tx.done;

            // Save to server
            try {
                await weeklyRosterService.saveRoster(rosterData);
            } catch (serverError) {
                console.error('Failed to save to server:', serverError);
            }

            showNotification(`Copied ${selectedEmployees.length} employees to current week (W${actualWeekNumber})`, 'success');

            // Navigate to current week
            setCurrentWeekNumber(actualWeekNumber);
            setCurrentYear(actualYear);
        } catch (error) {
            console.error('Error copying to current week:', error);
            showNotification('Error copying to current week', 'error');
        }
    };

    // Save roster
    const saveRoster = async () => {
        if (!isCurrentWeek) {
            showNotification('Can only edit current week roster', 'warning');
            return;
        }
        
        setSaving(true);
        try {
            const db = await initializeDB();
            const weekYear = `${currentYear}-W${String(currentWeekNumber).padStart(2, '0')}`;
            
            // Get current user information
            const userData = localStorage.getItem('user');
            let userId = null;
            let supervisorName = 'Unknown';
            
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    userId = user.id || user.userId || user.employeeId;
                    supervisorName = user.name || user.fullName || user.firstName + ' ' + user.lastName || 'Unknown';
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }
            
            // Separate selected employees into permanent and temp
            const permanentSelected = [];
            const tempSelected = [];
            
            selectedEmployees.forEach(id => {
                const employee = allEmployeesMap.get(id);
                if (employee) {
                    if (employee.type === 'temp') {
                        tempSelected.push(id);
                    } else {
                        permanentSelected.push(id);
                    }
                }
            });
            
            const rosterData = {
                weekYear,
                weekNumber: currentWeekNumber,
                year: currentYear,
                employees: selectedEmployees, // Keep for backward compatibility
                permanentEmployees: permanentSelected,
                temporaryEmployees: tempSelected,
                userId: userId,
                createdBy: userId,
                supervisorId: userId,
                supervisorName: supervisorName,
                dateCreated: hasCurrentWeekRoster ? undefined : new Date().toISOString(),
                dateModified: new Date().toISOString(),
                employeeCount: selectedEmployees.length,
                permanentCount: permanentSelected.length,
                tempCount: tempSelected.length
            };
            
            // Save to local database
            const tx = db.transaction(WEEK_LIST_STORE, 'readwrite');
            const store = tx.objectStore(WEEK_LIST_STORE);
            
            // If updating, preserve the original dateCreated
            const existing = await store.get(weekYear);
            if (existing && existing.dateCreated) {
                rosterData.dateCreated = existing.dateCreated;
            } else if (!rosterData.dateCreated) {
                rosterData.dateCreated = new Date().toISOString();
            }
            
            await store.put(rosterData);
            await tx.done;
            
            // Save to server
            try {
                console.log('Saving roster to server:', rosterData);
                await weeklyRosterService.saveRoster(rosterData);
                console.log('Roster saved to server successfully');
            } catch (serverError) {
                console.error('Failed to save to server, but local save succeeded:', serverError);
                // Don't show error to user since local save succeeded
            }
            
            setHasCurrentWeekRoster(true);
            showNotification(`Roster saved with ${selectedEmployees.length} employees`, 'success');
            
            // Dispatch custom event for other components
            window.dispatchEvent(new CustomEvent('weeklyRosterUpdated', { 
                detail: { 
                    weekYear, 
                    employeeCount: selectedEmployees.length,
                    permanentCount: permanentSelected.length,
                    tempCount: tempSelected.length
                } 
            }));
        } catch (error) {
            console.error('Error saving roster:', error);
            showNotification('Error saving roster', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Week navigation
    const handleWeekChange = (direction) => {
        const now = new Date();
        const actualWeekNumber = getISOWeek(now);
        const actualYear = now.getFullYear();
        
        if (direction === 'prev') {
            if (currentWeekNumber > 1) {
                setCurrentWeekNumber(currentWeekNumber - 1);
            } else {
                // Go to last week of previous year
                const prevYear = currentYear - 1;
                const lastWeekOfYear = getISOWeek(new Date(prevYear, 11, 31));
                setCurrentWeekNumber(lastWeekOfYear);
                setCurrentYear(prevYear);
            }
        } else if (direction === 'next') {
            // Prevent going to future weeks
            if (currentYear < actualYear || 
                (currentYear === actualYear && currentWeekNumber < actualWeekNumber)) {
                if (currentWeekNumber < 52) {
                    setCurrentWeekNumber(currentWeekNumber + 1);
                } else {
                    // Check if there's a week 53
                    const lastDayOfYear = new Date(currentYear, 11, 31);
                    const lastWeekOfYear = getISOWeek(lastDayOfYear);
                    
                    if (currentWeekNumber < lastWeekOfYear) {
                        setCurrentWeekNumber(currentWeekNumber + 1);
                    } else {
                        // Go to first week of next year
                        const nextYear = currentYear + 1;
                        if (nextYear < actualYear || 
                            (nextYear === actualYear && 1 <= actualWeekNumber)) {
                            setCurrentWeekNumber(1);
                            setCurrentYear(nextYear);
                        }
                    }
                }
            }
        } else if (direction === 'today') {
            setCurrentWeekNumber(actualWeekNumber);
            setCurrentYear(actualYear);
        }
    };

    // Check if next button should be disabled
    const isNextDisabled = useMemo(() => {
        const now = new Date();
        const actualWeekNumber = getISOWeek(now);
        const actualYear = now.getFullYear();
        
        return (currentYear > actualYear) || 
               (currentYear === actualYear && currentWeekNumber >= actualWeekNumber);
    }, [currentWeekNumber, currentYear]);

    // Selection handlers
    const handleEmployeeToggle = (id) => {
        if (!isCurrentWeek) return;
        
        setSelectedEmployees(prev => {
            if (prev.includes(id)) {
                return prev.filter(empId => empId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleSelectAll = () => {
        if (!isCurrentWeek) return;
        
        const currentTabEmployees = activeTab === 0 
            ? filteredEmployees 
            : filteredTemps;
        
        const currentTabIds = currentTabEmployees.map(emp => emp.id);
        const allSelected = currentTabIds.every(id => selectedEmployees.includes(id));

        if (allSelected) {
            // Deselect all from current tab
            setSelectedEmployees(prev => prev.filter(id => !currentTabIds.includes(id)));
        } else {
            // Select all from current tab
            setSelectedEmployees(prev => [...new Set([...prev, ...currentTabIds])]);
        }
    };

    const clearSelection = () => {
        if (!isCurrentWeek) return;
        setSelectedEmployees([]);
        showNotification('Selection cleared');
    };

    // Filter employees
    const filteredEmployees = useMemo(() => {
        return employees.filter(emp => {
            const matchesSearch = !searchTerm || 
                (emp.FullName || emp.fullName || emp.Name || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (emp.EmployeeId || emp.employeeId || emp.ID || '')?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const empDivision = emp.Division || emp.division || emp.DivisionCode || '';
            const matchesDivision = filterDivision === 'all' || empDivision === filterDivision;
            
            return matchesSearch && matchesDivision;
        });
    }, [employees, searchTerm, filterDivision]);

    // Filter temps
    const filteredTemps = useMemo(() => {
        return temps.filter(temp => {
            const matchesSearch = !searchTerm || 
                (temp.FullName || temp.fullName || temp.Name || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (temp.Agency || temp.agency || '')?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const tempAgency = temp.Agency || temp.agency || '';
            const matchesAgency = filterAgency === 'all' || tempAgency === filterAgency;
            
            return matchesSearch && matchesAgency;
        });
    }, [temps, searchTerm, filterAgency]);

    // Get selected employees details
    const selectedEmployeesDetails = useMemo(() => {
        return selectedEmployees
            .map(id => allEmployeesMap.get(id))
            .filter(Boolean);
    }, [selectedEmployees, allEmployeesMap]);

    // Columns for permanent employees - OPTIMIZED FOR TABLETS
    const employeeColumns = useMemo(() => {
        return [
            {
                field: 'select',
                headerName: '',
                width: 50,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <Checkbox
                        checked={selectedEmployees.includes(params.row.id)}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleEmployeeToggle(params.row.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        color="primary"
                        size="small"
                        disabled={!isCurrentWeek}
                    />
                ),
            },
            { 
                field: 'FullName', 
                headerName: 'Name', 
                flex: 1,
                minWidth: 100,
                valueGetter: (value, row) => row.FullName || row.fullName || row.Name || '-'
            },
            { 
                field: 'Division', 
                headerName: 'Division', 
                width: 80,
                valueGetter: (value, row) => row.Division || row.division || row.DivisionCode || '-'
            }
        ];
    }, [selectedEmployees, isCurrentWeek, handleEmployeeToggle]);

    // Columns for temp employees - OPTIMIZED FOR TABLETS
    const tempColumns = useMemo(() => {
        return [
            {
                field: 'select',
                headerName: '',
                width: 50,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <Checkbox
                        checked={selectedEmployees.includes(params.row.id)}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleEmployeeToggle(params.row.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        color="primary"
                        size="small"
                        disabled={!isCurrentWeek}
                    />
                ),
            },
            { 
                field: 'FullName', 
                headerName: 'Name', 
                flex: 1,
                minWidth: 100,
                valueGetter: (value, row) => row.FullName || row.fullName || row.Name || '-'
            },
            { 
                field: 'Agency', 
                headerName: 'Agency', 
                width: 80,
                valueGetter: (value, row) => row.Agency || row.agency || '-'
            }
        ];
    }, [selectedEmployees, isCurrentWeek, handleEmployeeToggle]);

    // Selected employees columns - OPTIMIZED FOR TABLETS
    const selectedColumns = useMemo(() => {
        return [
            { 
                field: 'type',
                headerName: 'Type',
                width: 60,
                renderCell: (params) => (
                    <Chip 
                        label={params.value === 'temp' ? 'Temp' : 'Perm'}
                        size="small"
                        color={params.value === 'temp' ? 'warning' : 'primary'}
                        sx={{ fontSize: '0.7rem' }}
                    />
                )
            },
            { 
                field: 'FullName', 
                headerName: 'Name', 
                flex: 1,
                minWidth: 100,
                valueGetter: (value, row) => row.FullName || row.fullName || row.Name || '-'
            },
            {
                field: 'actions',
                headerName: '',
                width: 50,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEmployeeToggle(params.row.id);
                        }}
                        disabled={!isCurrentWeek}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                ),
            }
        ];
    }, [isCurrentWeek, handleEmployeeToggle]);

    return (
        <Box sx={{ 
            height: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            p: { xs: 1, sm: 2, md: 3 }
        }}>
            {/* Header */}
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarTodayIcon color="primary" />
                            <Typography variant="h5" fontWeight="bold">
                                Week {currentWeekNumber} - {currentYear}
                            </Typography>
                            {!isCurrentWeek && (
                                <Chip 
                                    label="View Only" 
                                    size="small" 
                                    icon={<LockIcon />}
                                    color="warning"
                                />
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                            {currentWeekDates.start} - {currentWeekDates.end}
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <ButtonGroup size="small" variant="outlined">
                                <Button onClick={() => handleWeekChange('prev')} startIcon={<NavigateBeforeIcon />}>
                                    Previous
                                </Button>
                                <Button 
                                    onClick={() => handleWeekChange('today')} 
                                    startIcon={<TodayIcon />}
                                    disabled={isCurrentWeek}
                                >
                                    Current
                                </Button>
                                <Button 
                                    onClick={() => handleWeekChange('next')} 
                                    endIcon={<NavigateNextIcon />}
                                    disabled={isNextDisabled}
                                >
                                    Next
                                </Button>
                            </ButtonGroup>

                            {!isCurrentWeek && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={copyThisWeekToCurrent}
                                    startIcon={<ContentCopyIcon />}
                                    size="small"
                                    disabled={selectedEmployees.length === 0}
                                >
                                    Copy to Current Week
                                </Button>
                            )}

                            {isCurrentWeek && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={saveRoster}
                                    disabled={saving || selectedEmployees.length === 0}
                                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                >
                                    Save
                                </Button>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Filters - SINGLE ROW FOR TABLETS */}
            {isCurrentWeek && (
                <Paper elevation={1} sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={activeTab === 0 ? filterDivision : filterAgency}
                                    onChange={(e) => activeTab === 0 ? setFilterDivision(e.target.value) : setFilterAgency(e.target.value)}
                                    displayEmpty
                                >
                                    <MenuItem value="all">
                                        <em>{activeTab === 0 ? 'All Divisions' : 'All Agencies'}</em>
                                    </MenuItem>
                                    {(activeTab === 0 ? divisions : agencies).map(item => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={2} md={1}>
                            <Button
                                fullWidth
                                size="small"
                                variant="text"
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilterDivision('all');
                                    setFilterAgency('all');
                                }}
                            >
                                Clear
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={3} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <InfoIcon fontSize="small" />
                                {activeTab === 0 
                                    ? `${filteredEmployees.length} of ${employees.length} employees`
                                    : `${filteredTemps.length} of ${temps.length} temps`
                                }
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Main Content - OPTIMIZED LAYOUT */}
            <Grid container spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
                {/* Employee Selection - Only show for current week */}
                {isCurrentWeek && (
                    <Grid item xs={12} sm={6} md={6} sx={{ height: '100%', display: 'flex' }}>
                        <Paper elevation={2} sx={{ 
                            flex: 1,
                            display: 'flex', 
                            flexDirection: 'column',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="standard">
                                        <Tab 
                                            label={
                                                <Badge badgeContent={filteredEmployees.length} color="primary" max={999}>
                                                    <Typography variant="body2">Permanent</Typography>
                                                </Badge>
                                            } 
                                        />
                                        <Tab 
                                            label={
                                                <Badge badgeContent={filteredTemps.length} color="warning" max={999}>
                                                    <Typography variant="body2">Temporary</Typography>
                                                </Badge>
                                            } 
                                        />
                                    </Tabs>
                                    <Button
                                        size="small"
                                        onClick={handleSelectAll}
                                        variant="text"
                                    >
                                        {activeTab === 0 
                                            ? filteredEmployees.every(emp => selectedEmployees.includes(emp.id)) ? 'Deselect' : 'Select All'
                                            : filteredTemps.every(temp => selectedEmployees.includes(temp.id)) ? 'Deselect' : 'Select All'
                                        }
                                    </Button>
                                </Stack>
                            </Box>
                            
                            <Box sx={{ 
                                flex: 1, 
                                overflow: 'hidden',
                                position: 'relative',
                                '& .MuiDataGrid-root': {
                                    border: 'none',
                                    height: '100%',
                                },
                                '& .MuiDataGrid-cell': {
                                    padding: '0 8px',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    padding: '0 8px',
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                                }
                            }}>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <DataGrid
                                        rows={activeTab === 0 ? filteredEmployees : filteredTemps}
                                        columns={activeTab === 0 ? employeeColumns : tempColumns}
                                        initialState={{
                                            pagination: {
                                                paginationModel: { pageSize: 20 }
                                            }
                                        }}
                                        pageSizeOptions={[20, 50, 100]}
                                        checkboxSelection={false}
                                        disableRowSelectionOnClick
                                        density="compact"
                                        onRowClick={(params) => {
                                            handleEmployeeToggle(params.row.id);
                                        }}
                                        sx={{
                                            height: '100%',
                                            fontSize: '0.85rem',
                                            '& .MuiDataGrid-cell': {
                                                fontSize: '0.85rem',
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                fontSize: '0.85rem',
                                            },
                                            '& .MuiDataGrid-footerContainer': {
                                                fontSize: '0.8rem',
                                                minHeight: '40px',
                                                '& .MuiTablePagination-root': {
                                                    fontSize: '0.8rem',
                                                },
                                                '& .MuiTablePagination-selectLabel': {
                                                    fontSize: '0.8rem',
                                                },
                                                '& .MuiTablePagination-displayedRows': {
                                                    fontSize: '0.8rem',
                                                },
                                            },
                                            '& .MuiDataGrid-row': {
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? 'rgba(144, 202, 249, 0.16)' 
                                                        : 'rgba(25, 118, 210, 0.12)',
                                                }
                                            }
                                        }}
                                        getRowClassName={(params) =>
                                            selectedEmployees.includes(params.id) ? 'Mui-selected' : ''
                                        }
                                    />
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {/* Selected Employees - Full width when viewing past weeks */}
                <Grid item xs={12} sm={isCurrentWeek ? 6 : 12} md={isCurrentWeek ? 6 : 12} sx={{ height: '100%', display: 'flex' }}>
                    <Paper elevation={2} sx={{ 
                        flex: 1,
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden'
                    }}>
                        <Box sx={{ 
                            p: 1, 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            backgroundColor: theme.palette.grey[50],
                            ...(theme.palette.mode === 'dark' && {
                                backgroundColor: theme.palette.grey[900]
                            })
                        }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {isCurrentWeek ? 'Selected' : 'Roster'} ({selectedEmployees.length})
                                </Typography>
                                {isCurrentWeek && (
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={clearSelection}
                                        disabled={selectedEmployees.length === 0}
                                        variant="text"
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                        
                        <Box sx={{ 
                            flex: 1, 
                            overflow: 'hidden',
                            position: 'relative',
                            '& .MuiDataGrid-root': {
                                border: 'none',
                                height: '100%',
                            },
                            '& .MuiDataGrid-cell': {
                                padding: '0 8px',
                            },
                            '& .MuiDataGrid-columnHeader': {
                                padding: '0 8px',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid rgba(224, 224, 224, 1)',
                            }
                        }}>
                            {selectedEmployeesDetails.length === 0 ? (
                                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" p={3}>
                                    <GroupsIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        {isCurrentWeek ? 'No employees selected' : 'No roster for this week'}
                                    </Typography>
                                    {isCurrentWeek && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                            Select employees from the left panel
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <DataGrid
                                    rows={selectedEmployeesDetails}
                                    columns={selectedColumns}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { pageSize: 20 }
                                        }
                                    }}
                                    pageSizeOptions={[20, 50, 100]}
                                    disableRowSelectionOnClick
                                    density="compact"
                                    sx={{
                                        height: '100%',
                                        fontSize: '0.85rem',
                                        '& .MuiDataGrid-cell': {
                                            fontSize: '0.85rem',
                                        },
                                        '& .MuiDataGrid-cell:focus': {
                                            outline: 'none',
                                        },
                                        '& .MuiDataGrid-columnHeaders': {
                                            fontSize: '0.85rem',
                                        },
                                        '& .MuiDataGrid-footerContainer': {
                                            fontSize: '0.8rem',
                                            minHeight: '40px',
                                            '& .MuiTablePagination-root': {
                                                fontSize: '0.8rem',
                                            },
                                            '& .MuiTablePagination-selectLabel': {
                                                fontSize: '0.8rem',
                                            },
                                            '& .MuiTablePagination-displayedRows': {
                                                fontSize: '0.8rem',
                                            },
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        
                        {/* Summary Footer */}
                        {selectedEmployeesDetails.length > 0 && (
                            <Box sx={{ 
                                p: 1, 
                                borderTop: 1, 
                                borderColor: 'divider',
                                backgroundColor: theme.palette.grey[50],
                                ...(theme.palette.mode === 'dark' && {
                                    backgroundColor: theme.palette.grey[900]
                                })
                            }}>
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Chip 
                                        label={`Permanent: ${selectedEmployeesDetails.filter(e => e.type === 'permanent').length}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Chip 
                                        label={`Temporary: ${selectedEmployeesDetails.filter(e => e.type === 'temp').length}`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                    />
                                    <Chip 
                                        label={`Total: ${selectedEmployeesDetails.length}`}
                                        size="small"
                                        color="success"
                                        variant="filled"
                                    />
                                </Stack>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={3000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setNotification({ ...notification, open: false })} 
                    severity={notification.severity}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default WeeklyRosterImproved;